import React from 'react';
import { Target, Users, Lightbulb, TrendingUp } from 'lucide-react';
import { Card, CardContent } from './ui/card';

const AboutSection = () => {
  const values = [
    {
      icon: Target,
      title: 'Precision Planning',
      description: 'Meticulous attention to detail in every project phase, ensuring optimal resource allocation and timeline management.',
    },
    {
      icon: Users,
      title: 'Team Collaboration',
      description: 'Fostering seamless communication and coordination across all stakeholders for unified project success.',
    },
    {
      icon: Lightbulb,
      title: 'Innovative Solutions',
      description: 'Applying cutting-edge methodologies and adaptive strategies to overcome complex project challenges.',
    },
    {
      icon: TrendingUp,
      title: 'Continuous Growth',
      description: 'Commitment to excellence through ongoing improvement, learning, and adaptation to industry best practices.',
    },
  ];

  return (
    <section id="about" className="py-24 bg-white relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-[#8B2635]/5 to-transparent rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-[#D4A574]/5 to-transparent rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16 animate-fade-in-up">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            About <span className="text-[#8B2635]">Sperah</span>
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-[#8B2635] to-[#A63446] mx-auto mb-8" />
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Sperah Project Management is a comprehensive solution designed to streamline project workflows, 
            enhance team collaboration, and deliver exceptional results. With our expertise in project planning, 
            development, and execution, we ensure your projects stay on track, on budget, and exceed expectations.
          </p>
        </div>

        {/* Core Values Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mt-16">
          {values.map((value, index) => {
            const Icon = value.icon;
            return (
              <Card
                key={index}
                className="group hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 border-0 bg-gradient-to-br from-white to-gray-50 overflow-hidden animate-fade-in-up"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <CardContent className="p-8 relative">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-[#8B2635]/5 to-transparent rounded-bl-full transform translate-x-8 -translate-y-8 group-hover:scale-150 transition-transform duration-500" />
                  <div className="relative z-10">
                    <div className="w-16 h-16 bg-gradient-to-br from-[#8B2635] to-[#A63446] rounded-2xl flex items-center justify-center mb-6 transform group-hover:rotate-6 transition-transform duration-300">
                      <Icon className="h-8 w-8 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-4">{value.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{value.description}</p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-20">
          {[
            { value: '500+', label: 'Projects Delivered' },
            { value: '98%', label: 'Client Satisfaction' },
            { value: '50+', label: 'Expert Team Members' },
            { value: '15+', label: 'Years Experience' },
          ].map((stat, index) => (
            <div key={index} className="text-center animate-fade-in-up" style={{ animationDelay: `${index * 0.1}s` }}>
              <div className="text-4xl md:text-5xl font-black text-[#8B2635] mb-2">{stat.value}</div>
              <div className="text-gray-600 font-medium">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AboutSection;